import React, { useState, useEffect } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { cufGenerationService, CUFGenerationData } from '../../services/cufGenerationService';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface CUFGenerationChartProps {
  blockId: string;
  dateRange?: string;
}

export const CUFGenerationChart: React.FC<CUFGenerationChartProps> = ({
  blockId,
  dateRange = '2024-10-01_to_2024-10-10'
}) => {
  const [data, setData] = useState<CUFGenerationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await cufGenerationService.getTestData();
        setData(result);
      } catch (err) {
        setError('Failed to load CUF vs Generation data');
        console.error('Error fetching CUF generation data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [blockId, dateRange]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{formatDate(label)}</p>
          <p className="text-blue-600">
            Generation: <span className="font-medium">{data.generation_kwh.toFixed(1)} kWh</span>
          </p>
          <p className="text-purple-600">
            CUF: <span className="font-medium">{data.cuf.toFixed(1)}%</span>
          </p>
          {data.is_anomaly && (
            <p className="text-red-600 font-medium">⚠️ Anomaly Detected</p>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="w-full h-96">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="date" 
            tickFormatter={formatDate}
            stroke="#666"
            fontSize={12}
          />
          <YAxis 
            yAxisId="generation"
            orientation="left"
            stroke="#3b82f6"
            fontSize={12}
            label={{ value: 'Generation (kWh)', angle: -90, position: 'insideLeft' }}
          />
          <YAxis 
            yAxisId="cuf"
            orientation="right"
            stroke="#8b5cf6"
            fontSize={12}
            label={{ value: 'CUF (%)', angle: 90, position: 'insideRight' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          <Bar
            yAxisId="generation"
            dataKey="generation_kwh"
            fill="#93c5fd"
            name="Generation (kWh)"
            radius={[2, 2, 0, 0]}
          />
          
          <Line
            yAxisId="cuf"
            type="monotone"
            dataKey="cuf"
            stroke="#8b5cf6"
            strokeWidth={3}
            dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#8b5cf6', strokeWidth: 2 }}
            name="CUF (%)"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};